import { Info } from 'lucide-react';
import { cn } from '@/components/atoms/primitives/utils';

export type MissingDataNoticeVariant = 'inline' | 'card' | 'section';

export interface MissingDataNoticeProps {
  /** Display variant */
  variant?: MissingDataNoticeVariant;
  /** Custom message override */
  message?: string;
  /** Optional field name for contextual messaging (e.g. "Venue capacity") */
  field?: string;
  /** Additional CSS classes */
  className?: string;
}

const DEFAULT_MESSAGE = "We're working on getting this info";

function buildMessage(message?: string, field?: string): string {
  if (message) return message;
  if (field) return `${field} info coming soon`;
  return DEFAULT_MESSAGE;
}

export function MissingDataNotice({
  variant = 'inline',
  message,
  field,
  className,
}: MissingDataNoticeProps) {
  const text = buildMessage(message, field);

  if (variant === 'inline') {
    return (
      <span
        className={cn('inline-flex items-center gap-1 text-sm text-muted-foreground', className)}
        data-testid="missing-data-notice"
      >
        <Info size={14} className="shrink-0" aria-hidden="true" />
        {text}
      </span>
    );
  }

  if (variant === 'card') {
    return (
      <div
        className={cn(
          'flex min-h-20 items-center justify-center gap-2 rounded-lg border border-dashed border-muted-foreground/25 bg-muted/30 px-4 py-6 text-sm text-muted-foreground',
          className,
        )}
        data-testid="missing-data-notice"
      >
        <Info size={16} className="shrink-0" aria-hidden="true" />
        {text}
      </div>
    );
  }

  // section variant
  return (
    <div
      className={cn(
        'flex w-full items-center justify-center gap-2 border-y border-dashed border-muted-foreground/25 bg-muted/20 px-4 py-4 text-sm text-muted-foreground',
        className,
      )}
      data-testid="missing-data-notice"
    >
      <Info size={16} className="shrink-0" aria-hidden="true" />
      {text}
    </div>
  );
}
